import {
  getSegments,
  getLayoverDuration,
  getFullJourneyDuration,
  minsToHM,
  formatApiDateTime,
} from "../../utils/flightUtils";
import { useCurrency } from "../../context/CurrencyContext";
import { formatPriceSync } from "../../utils/currency";

const FlightDetails = ({
  // New props from FlightCard (passed as segments)
  segments: segmentsProp,
  
  // Legacy props
  flight,
  index,
  expandedFlights,
}) => {
  const { currency } = useCurrency();
  // If using legacy pattern with flight and index
  if (flight && index !== undefined && expandedFlights && !expandedFlights[index]) {
    return null;
  }

  // Determine segments based on input
  let segments = segmentsProp;
  if (!segments && flight && flight.Flights) {
    segments = getSegments(flight.Flights?.Onward) || [];
  }
  
  // Add defensive checks for segments data
  if (!segments || segments.length === 0) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500 text-center">Flight data not available</p>
      </div>
    );
  }

  const first = segments[0];
  const last = segments[segments.length - 1];
  const fullDuration = getFullJourneyDuration(segments) || 0;

  // Parse baggage from API response precisely
  const parseBaggage = (baggageStr) => {
    if (!baggageStr) return { weight: "N/A", pieceCount: null, fullText: "N/A" };
    
    // Remove || separator
    const cleaned = baggageStr.replace(/\|\|/g, "").trim();
    
    // Extract piece count if available (e.g., "15 Kg (01 Piece only)")
    const pieceMatch = cleaned.match(/\((\d+)\s*Piece\s*only\)/i);
    const pieceCount = pieceMatch ? pieceMatch[1] : null;
    
    // Extract weight (remove piece count part if present)
    const weight = cleaned.replace(/\s*\(.*?\)/i, "").trim();
    
    return {
      weight: weight || "N/A",
      pieceCount: pieceCount,
      fullText: cleaned || "N/A"
    };
  };

  // Get fare data from flight object
  const fareData = flight?.Fare || flight?.fare || {};
  const validationData = flight?.Validation || flight?.validation || {};
  const totalData = fareData?.total || {};

  // Format refundable details
  const refundableDetails = fareData?.refundType === "P"
    ? `Airline: ${fareData?.cancelChgAir || "N/A"}, FTD: ${fareData?.cancelChgFTD || "N/A"}`
    : "Not Refundable";

  return (
    <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* FLIGHT INFORMATION */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 text-base border-b border-gray-200 pb-2">
            Flight Information
          </h4>
          <div className="space-y-2 text-sm">
            <Info label="Airline" value={first?.airName || "N/A"} />
            <Info label="Flight Number" value={first?.flightNo || "N/A"} />
            <Info label="Marketing Airline Code" value={first?.airCode || "N/A"} />
            <Info label="Operating Airline" value={first?.airNameOp || first?.airCodeOp || "N/A"} />
            {first?.airCodeOp && first?.airCodeOp !== first?.airCode && (
              <Info label="Operating Airline Code" value={first?.airCodeOp} />
            )}
            <Info label="Aircraft Type" value={first?.airType || "N/A"} />
            <Info label="Cabin Class" value={first?.cabin || "N/A"} />
            <Info label="Fare Class" value={first?.fareClass || "N/A"} />
            <Info label="Flight ID" value={first?.flightID || "N/A"} />
            
            <div className="pt-2 mt-2 border-t border-gray-100">
              <Info label="Total Flight Time" value={minsToHM(fullDuration)} />
              <Info label="Number of Stops" value={segments.length - 1} />
            </div>

            {/* Show each segment duration */}
            {segments.length > 1 && (
              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-700 mb-2">Segment Durations:</div>
                {segments.map((seg, i) => (
                  <Info
                    key={i}
                    label={`Segment ${i + 1}`}
                    value={minsToHM(parseInt(seg.duration || 0))}
                  />
                ))}
              </div>
            )}

            {/* Layover times */}
            {segments.length > 1 && (
              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-700 mb-2">Layover Times:</div>
                {segments.map((seg, i) => {
                  if (i === segments.length - 1) return null;
                  const layover = getLayoverDuration(segments, i);
                  return (
                    <Info
                      key={`layover-${i}`}
                      label={`Layover ${i + 1} (${seg.arrCode} → ${segments[i + 1]?.depCode})`}
                      value={layover ? minsToHM(layover) : "N/A"}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ROUTE INFORMATION */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 text-base border-b border-gray-200 pb-2">
            Route Information
          </h4>
          <div className="space-y-2 text-sm">
            <div className="pb-2 border-b border-gray-100">
              <div className="text-xs font-semibold text-gray-700 mb-2">Departure:</div>
              <Info
                label="City"
                value={first?.depCName || "N/A"}
              />
              <Info
                label="Airport"
                value={first?.depAName || "N/A"}
              />
              <Info
                label="Code"
                value={first?.depCode || "N/A"}
              />
              <Info 
                label="Terminal" 
                value={first?.depTer ? `Terminal ${first.depTer}` : "N/A"} 
              />
              <Info
                label="Date & Time"
                value={formatApiDateTime(first?.depDate) || "N/A"}
              />
            </div>

            <div className="pt-2">
              <div className="text-xs font-semibold text-gray-700 mb-2">Arrival:</div>
              <Info
                label="City"
                value={last?.arrCName || "N/A"}
              />
              <Info
                label="Airport"
                value={last?.arrAName || "N/A"}
              />
              <Info
                label="Code"
                value={last?.arrCode || "N/A"}
              />
              <Info 
                label="Terminal" 
                value={last?.arrTer ? `Terminal ${last.arrTer}` : "N/A"} 
              />
              <Info
                label="Date & Time"
                value={formatApiDateTime(last?.arrDate) || "N/A"}
              />
            </div>

            {/* Show all segments if multiple */}
            {segments.length > 1 && (
              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-700 mb-2">All Segments:</div>
                {segments.map((seg, i) => (
                  <div key={i} className="mb-3 p-2 bg-gray-50 rounded text-xs">
                    <div className="font-medium text-gray-800 mb-1">
                      Segment {i + 1}: {seg.depCode} → {seg.arrCode}
                    </div>
                    <div className="text-gray-600 space-y-1">
                      <div>Flight: {seg.flightNo}</div>
                      <div>Duration: {minsToHM(parseInt(seg.duration || 0))}</div>
                      {seg.depTer && <div>Dep Terminal: {seg.depTer}</div>}
                      {seg.arrTer && <div>Arr Terminal: {seg.arrTer}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* FARE INFORMATION */}
        {Object.keys(fareData).length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 text-base border-b border-gray-200 pb-2">
              Fare Information
            </h4>
            <div className="space-y-2 text-sm">
              <Info label="Fare Type" value={fareData?.fareTypeDesc || "N/A"} />
              {fareData?.fareTypeInd && (
                <Info label="Fare Type Index" value={fareData.fareTypeInd} />
              )}
              {fareData?.popMsg && (
                <Info label="Special Message" value={fareData.popMsg} />
              )}
              
              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-700 mb-2">Pricing:</div>
                {totalData?.base && (
                  <Info label="Base Fare" value={formatPriceSync(parseInt(totalData.base), currency)} />
                )}
                {totalData?.tax && (
                  <Info label="Taxes & Fees" value={formatPriceSync(parseInt(totalData.tax), currency)} />
                )}
                {totalData?.total && (
                  <Info label="Total Price" value={formatPriceSync(parseInt(totalData.total), currency)} />
                )}
                {totalData?.netfare && (
                  <Info label="Net Fare" value={formatPriceSync(parseInt(totalData.netfare), currency)} />
                )}
                {totalData?.inc && (
                  <Info label="Inclusive Charges" value={formatPriceSync(parseInt(totalData.inc), currency)} />
                )}
                {totalData?.tds && (
                  <Info label="TDS" value={formatPriceSync(parseInt(totalData.tds), currency)} />
                )}
                {totalData?.agentMarkup && (
                  <Info label="Agent Markup" value={formatPriceSync(parseFloat(totalData.agentMarkup), currency)} />
                )}
              </div>

              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-700 mb-2">Refund Policy:</div>
                <Info
                  label="Refund Type"
                  value={
                    fareData?.refundType === "P"
                      ? "Partially Refundable"
                      : fareData?.refundType === "N"
                      ? "Non Refundable"
                      : "N/A"
                  }
                />
                {fareData?.refundType === "P" && (
                  <>
                    {fareData?.cancelChgAir && (
                      <Info label="Airline Cancellation" value={fareData.cancelChgAir} />
                    )}
                    {fareData?.cancelChgFTD && (
                      <Info label="FTD Cancellation" value={fareData.cancelChgFTD} />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* BAGGAGE & VALIDATION */}
        {(Object.keys(fareData).length > 0 || Object.keys(validationData).length > 0) && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 text-base border-b border-gray-200 pb-2">
              Baggage & Additional Info
            </h4>
            <div className="space-y-3 text-sm">
              {/* Baggage Information - Enhanced Display */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="text-xs font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Baggage Allowance
                </div>
                
                {fareData?.bagCkin && (() => {
                  const checkinBaggage = parseBaggage(fareData.bagCkin);
                  return (
                    <div className="mb-3 pb-3 border-b border-blue-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-xs font-semibold text-gray-700 mb-1">Check-in Baggage:</div>
                          <div className="text-sm font-bold text-gray-900">
                            {checkinBaggage.weight}
                            {checkinBaggage.pieceCount && (
                              <span className="ml-2 text-xs font-normal text-gray-600">
                                ({checkinBaggage.pieceCount} Piece only)
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="ml-2">
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {fareData?.bagCbin && (() => {
                  const cabinBaggage = parseBaggage(fareData.bagCbin);
                  return (
                    <div className="mb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-xs font-semibold text-gray-700 mb-1">Cabin Baggage:</div>
                          <div className="text-sm font-bold text-gray-900">
                            {cabinBaggage.weight}
                          </div>
                        </div>
                        <div className="ml-2">
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {fareData?.seats && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-gray-700">Seats Available:</div>
                      <div className="text-sm font-bold text-gray-900">{fareData.seats}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Validation Information */}
              {Object.keys(validationData).length > 0 && (
                <div className="pt-2 mt-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Validation:</div>
                  {validationData?.lcc !== undefined && (
                    <Info 
                      label="Low Cost Carrier" 
                      value={validationData.lcc === "1" || validationData.lcc === 1 ? "Yes" : "No"} 
                    />
                  )}
                  {validationData?.freeMeal !== undefined && (
                    <Info 
                      label="Free Meal" 
                      value={validationData.freeMeal === "1" || validationData.freeMeal === 1 ? "Yes" : "No"} 
                    />
                  )}
                  {validationData?.gstInd !== undefined && (
                    <Info
                      label="GST Applicable"
                      value={
                        validationData.gstInd === "2"
                          ? "Optional"
                          : validationData.gstInd === "1"
                          ? "Mandatory"
                          : validationData.gstInd === "0"
                          ? "Not Allowed"
                          : "N/A"
                      }
                    />
                  )}
                  {validationData?.allowFQT !== undefined && (
                    <Info 
                      label="Allow FQT" 
                      value={validationData.allowFQT === "1" || validationData.allowFQT === 1 ? "Yes" : "No"} 
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Small reusable component with better null handling
const Info = ({ label, value }) => (
  <div className="flex justify-between py-1">
    <span className="text-gray-600">{label}:</span>
    <span className="font-medium text-gray-900 text-right">{value || value === 0 ? value : "N/A"}</span>
  </div>
);

export default FlightDetails;
